/**
 * P&L Demo Data for Investor Presentations
 * @feature VITE_INVESTOR_DEMO
 * @task B.INV.PL - P&L Analysis Table
 */

export interface PLLineItem {
  id: string;
  label: string;
  category:
    | 'revenue'
    | 'cogs'
    | 'gross_profit'
    | 'opex_header'
    | 'opex_detail'
    | 'opex_total'
    | 'ebitda'
    | 'depreciation'
    | 'ebit'
    | 'interest'
    | 'tax'
    | 'net_income';
  indent: 0 | 1 | 2;
  isSubtotal?: boolean;
  isTotal?: boolean;
  isHeader?: boolean;
  showMargin?: boolean;
  values: number[];
}

export interface PLMonth {
  key: string;
  label: string;
}

export interface PLData {
  months: PLMonth[];
  lineItems: PLLineItem[];
  currency: 'USD' | 'BRL';
}

// US Market P&L Data
export const plDemoDataUS: PLData = {
  currency: 'USD',
  months: [
    { key: 'aug', label: 'Aug' },
    { key: 'sep', label: 'Sep' },
    { key: 'oct', label: 'Oct' },
    { key: 'nov', label: 'Nov' },
    { key: 'dec', label: 'Dec' },
    { key: 'jan', label: 'Jan' },
  ],
  lineItems: [
    {
      id: 'revenue',
      label: 'Revenue',
      category: 'revenue',
      indent: 0,
      isTotal: true,
      showMargin: false,
      values: [125000, 142000, 138000, 165000, 189000, 210000],
    },
    {
      id: 'cogs',
      label: 'Cost of Goods Sold',
      category: 'cogs',
      indent: 0,
      values: [37500, 42600, 41400, 49500, 56700, 63000],
    },
    {
      id: 'gross_profit',
      label: 'Gross Profit',
      category: 'gross_profit',
      indent: 0,
      isSubtotal: true,
      showMargin: true,
      values: [87500, 99400, 96600, 115500, 132300, 147000],
    },
    {
      id: 'opex_header',
      label: 'Operating Expenses',
      category: 'opex_header',
      indent: 0,
      isHeader: true,
      values: [0, 0, 0, 0, 0, 0],
    },
    {
      id: 'opex_salaries',
      label: 'Salaries & Wages',
      category: 'opex_detail',
      indent: 1,
      values: [35000, 36000, 36000, 38000, 40000, 42000],
    },
    {
      id: 'opex_marketing',
      label: 'Marketing & Advertising',
      category: 'opex_detail',
      indent: 1,
      values: [8500, 9200, 8800, 10500, 12000, 13500],
    },
    {
      id: 'opex_rent',
      label: 'Rent & Utilities',
      category: 'opex_detail',
      indent: 1,
      values: [12000, 12000, 12000, 12000, 12000, 12000],
    },
    {
      id: 'opex_software',
      label: 'Software & Technology',
      category: 'opex_detail',
      indent: 1,
      values: [5500, 5800, 6000, 6200, 6500, 7000],
    },
    {
      id: 'opex_other',
      label: 'Other Operating Expenses',
      category: 'opex_detail',
      indent: 1,
      values: [4500, 4800, 5000, 5300, 5500, 6000],
    },
    {
      id: 'total_opex',
      label: 'Total Operating Expenses',
      category: 'opex_total',
      indent: 0,
      isSubtotal: true,
      values: [65500, 67800, 67800, 72000, 76000, 80500],
    },
    {
      id: 'ebitda',
      label: 'EBITDA',
      category: 'ebitda',
      indent: 0,
      isSubtotal: true,
      showMargin: true,
      values: [22000, 31600, 28800, 43500, 56300, 66500],
    },
    {
      id: 'depreciation',
      label: 'Depreciation & Amortization',
      category: 'depreciation',
      indent: 0,
      values: [2500, 2500, 2500, 2500, 2500, 2500],
    },
    {
      id: 'ebit',
      label: 'Operating Income (EBIT)',
      category: 'ebit',
      indent: 0,
      isSubtotal: true,
      showMargin: true,
      values: [19500, 29100, 26300, 41000, 53800, 64000],
    },
    {
      id: 'interest',
      label: 'Interest Expense',
      category: 'interest',
      indent: 0,
      values: [1200, 1200, 1200, 1200, 1200, 1200],
    },
    {
      id: 'tax',
      label: 'Income Tax',
      category: 'tax',
      indent: 0,
      values: [4575, 6975, 6275, 9950, 13150, 15700],
    },
    {
      id: 'net_income',
      label: 'Net Income',
      category: 'net_income',
      indent: 0,
      isTotal: true,
      showMargin: true,
      values: [13725, 20925, 18825, 29850, 39450, 47100],
    },
  ],
};

// Brazilian Market P&L Data (values in BRL, ~5x USD)
export const plDemoDataBR: PLData = {
  currency: 'BRL',
  months: [
    { key: 'aug', label: 'Ago' },
    { key: 'sep', label: 'Set' },
    { key: 'oct', label: 'Out' },
    { key: 'nov', label: 'Nov' },
    { key: 'dec', label: 'Dez' },
    { key: 'jan', label: 'Jan' },
  ],
  lineItems: [
    {
      id: 'revenue',
      label: 'Receita',
      category: 'revenue',
      indent: 0,
      isTotal: true,
      showMargin: false,
      values: [625000, 710000, 690000, 825000, 945000, 1050000],
    },
    {
      id: 'cogs',
      label: 'Custo dos Produtos Vendidos',
      category: 'cogs',
      indent: 0,
      values: [187500, 213000, 207000, 247500, 283500, 315000],
    },
    {
      id: 'gross_profit',
      label: 'Lucro Bruto',
      category: 'gross_profit',
      indent: 0,
      isSubtotal: true,
      showMargin: true,
      values: [437500, 497000, 483000, 577500, 661500, 735000],
    },
    {
      id: 'opex_header',
      label: 'Despesas Operacionais',
      category: 'opex_header',
      indent: 0,
      isHeader: true,
      values: [0, 0, 0, 0, 0, 0],
    },
    {
      id: 'opex_salaries',
      label: 'Salários e Encargos',
      category: 'opex_detail',
      indent: 1,
      values: [175000, 180000, 180000, 190000, 200000, 210000],
    },
    {
      id: 'opex_marketing',
      label: 'Marketing e Publicidade',
      category: 'opex_detail',
      indent: 1,
      values: [42500, 46000, 44000, 52500, 60000, 67500],
    },
    {
      id: 'opex_rent',
      label: 'Aluguel e Utilidades',
      category: 'opex_detail',
      indent: 1,
      values: [60000, 60000, 60000, 60000, 60000, 60000],
    },
    {
      id: 'opex_software',
      label: 'Software e Tecnologia',
      category: 'opex_detail',
      indent: 1,
      values: [27500, 29000, 30000, 31000, 32500, 35000],
    },
    {
      id: 'opex_other',
      label: 'Outras Despesas Operacionais',
      category: 'opex_detail',
      indent: 1,
      values: [22500, 24000, 25000, 26500, 27500, 30000],
    },
    {
      id: 'total_opex',
      label: 'Total Despesas Operacionais',
      category: 'opex_total',
      indent: 0,
      isSubtotal: true,
      values: [327500, 339000, 339000, 360000, 380000, 402500],
    },
    {
      id: 'ebitda',
      label: 'EBITDA',
      category: 'ebitda',
      indent: 0,
      isSubtotal: true,
      showMargin: true,
      values: [110000, 158000, 144000, 217500, 281500, 332500],
    },
    {
      id: 'depreciation',
      label: 'Depreciação e Amortização',
      category: 'depreciation',
      indent: 0,
      values: [12500, 12500, 12500, 12500, 12500, 12500],
    },
    {
      id: 'ebit',
      label: 'Lucro Operacional (EBIT)',
      category: 'ebit',
      indent: 0,
      isSubtotal: true,
      showMargin: true,
      values: [97500, 145500, 131500, 205000, 269000, 320000],
    },
    {
      id: 'interest',
      label: 'Despesas Financeiras',
      category: 'interest',
      indent: 0,
      values: [6000, 6000, 6000, 6000, 6000, 6000],
    },
    {
      id: 'tax',
      label: 'Imposto de Renda',
      category: 'tax',
      indent: 0,
      values: [22875, 34875, 31375, 49750, 65750, 78500],
    },
    {
      id: 'net_income',
      label: 'Lucro Líquido',
      category: 'net_income',
      indent: 0,
      isTotal: true,
      showMargin: true,
      values: [68625, 104625, 94125, 149250, 197250, 235500],
    },
  ],
};
